<template>
  <div class="recomendacao-produtos-container">
    <!-- Loading: aguardar status da campanha antes de exibir a página -->
    <div v-if="!statusCarregado" class="status-loading text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <p class="mt-3 text-muted mb-0">Carregando status da campanha...</p>
      <small class="text-muted">Verificando se a campanha está ativa ou desativada</small>
    </div>

    <template v-else>
    <!-- Header com Breadcrumb -->
    <div class="page-header mb-3">
      <div class="d-flex align-items-center justify-content-between">
        <div class="flex-grow-1">
          <b-breadcrumb class="breadcrumb-custom mb-2">
            <b-breadcrumb-item :to="{ name: 'campaign-ia' }">
              <feather-icon icon="ZapIcon" size="16" />
              Campanhas Inteligentes
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              Recomendação de Produtos
            </b-breadcrumb-item>
          </b-breadcrumb>
          <h2 class="page-title mb-1">
            <feather-icon
              icon="TrendingUpIcon"
              size="28"
              class="text-primary mr-2"
            />
            Recomendação de Produtos com IA
          </h2>
          <p class="text-muted mb-0">
            Configure campanhas automatizadas de recomendação baseadas em machine learning
          </p>
        </div>
      </div>
    </div>

    <!-- Progress Steps - Estilo Simples -->
    <b-card class="steps-card mb-4">
      <div class="steps-header mb-3">
        <h6 class="mb-0 text-muted">
          Etapa {{ etapaAtual + 1 }} de {{ steps.length }}
        </h6>
      </div>
      <div class="steps-navigation">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="step-indicator"
          :class="{
            'step-active': etapaAtual === index,
            'step-completed': etapaAtual > index,
            'step-pending': etapaAtual < index
          }"
        />
      </div>
    </b-card>

    <!-- Conteúdo Principal -->
    <!-- Campanha Ativa - Card Bonito (estilo igual ao de Aniversário) -->
    <div v-if="statusCampanha && statusCampanha.ativo" class="campanha-ativa-section">
      <b-card class="campanha-card shadow-sm mb-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center">
            <div class="status-badge-active mr-3">
              <feather-icon icon="CheckCircleIcon" size="24" />
            </div>
            <div>
              <h4 class="mb-0">Campanha de Recomendação Ativa</h4>
              <small class="text-success">
                <feather-icon icon="ActivityIcon" size="14" />
                Recomendações Automáticas Ativas
              </small>
            </div>
          </div>
          <b-button
            variant="danger"
            size="sm"
            @click="desativarCampanha"
            :disabled="carregando"
          >
            <b-spinner v-if="carregando" small class="mr-1" />
            <feather-icon v-else icon="PauseIcon" size="14" class="mr-50" />
            Desativar
          </b-button>
        </div>

        <!-- Configurações da Campanha -->
        <b-row class="config-info">
          <b-col md="4" class="mb-3">
            <div class="info-item">
              <feather-icon icon="FileTextIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Template WhatsApp</small>
                <strong>{{ campanhaAtiva ? campanhaAtiva.template_name : 'Template configurado' }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="info-item">
              <feather-icon icon="ShoppingBagIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Produtos Recomendados</small>
                <strong>{{ campanhaAtiva ? campanhaAtiva.num_recommendations : 3 }} produtos</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="info-item">
              <feather-icon icon="ClockIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Horário de Envio</small>
                <strong v-if="campanhaAtiva && campanhaAtiva.send_time">
                  {{ campanhaAtiva.send_time }}
                </strong>
                <strong v-else>—</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="info-item">
              <feather-icon icon="CalendarIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Dias da Semana</small>
                <strong v-if="campanhaAtiva && campanhaAtiva.send_days">
                  {{ formatarDiasSemana(campanhaAtiva.send_days) }}
                </strong>
                <strong v-else>Todos os dias</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="info-item">
              <feather-icon icon="TargetIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Confiança Mínima</small>
                <strong>{{ campanhaAtiva && campanhaAtiva.min_confidence ? (campanhaAtiva.min_confidence * 100).toFixed(0) : 60 }}%</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="info-item">
              <feather-icon icon="CpuIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Algoritmo</small>
                <strong>{{ campanhaAtiva ? traduzirAlgoritmo(campanhaAtiva.algorithm) : 'Híbrido' }}</strong>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Botão Ver Métricas -->
        <div class="mt-3 text-center">
          <b-button
            variant="primary"
            @click="irParaMetricas"
          >
            <feather-icon icon="BarChart2Icon" size="16" class="mr-50" />
            Ver Métricas Completas
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Configuração de Nova Campanha (quando não está ativa) -->
    <b-row v-else>
      <!-- Coluna de Configurações -->
      <b-col lg="7" class="mb-3">
        <b-card class="config-card shadow-sm">

          <div class="step-header mb-4">
            <div class="d-flex align-items-center">
              <div class="step-number-badge mr-3">
                {{ etapaAtual + 1 }}
              </div>
              <div>
                <h5 class="mb-0">{{ steps[etapaAtual].titulo }}</h5>
                <small class="text-muted">{{ steps[etapaAtual].descricao }}</small>
              </div>
            </div>
            <b-spinner v-if="carregando" small class="ml-2" />
          </div>

          <!-- ETAPA 0: Informações Básicas -->
          <div v-show="etapaAtual === 0" class="etapa-content">
            <!-- Tipo de Mensagem -->
            <b-form-group label="Tipo de Mensagem" label-class="font-weight-bold">
              <b-form-radio-group
                v-model="form.tipoMensagem"
                :options="tipoMensagemOptions"
                button-variant="outline-primary"
                buttons
                size="sm"
              />
            </b-form-group>

            <!-- Informação da Loja Selecionada -->
            <b-alert v-if="lojaSelecionada" variant="info" show class="loja-info">
              <div class="d-flex align-items-center">
                <feather-icon icon="StoreIcon" size="18" class="mr-2" />
                <div>
                  <strong>Loja Selecionada:</strong> {{ nomeLojaSelecionada }}
                  <br>
                  <small class="text-muted">
                    Configurando recomendações para esta loja
                  </small>
                </div>
              </div>
            </b-alert>

            <div class="info-box mt-4">
              <feather-icon icon="InfoIcon" size="20" class="text-primary mr-2" />
              <div>
                <strong>Sobre o Tipo de Mensagem</strong>
                <p class="mb-2 mt-2">
                  Na recomendação de produtos de moda, o sistema entende o que o cliente já comprou e passa a sugerir:
                </p>
                <ul class="mb-2 pl-3">
                  <li>Peças que combinam com o que ele já tem</li>
                  <li>Itens parecidos com o estilo dele</li>
                  <li>Novidades da mesma categoria ou marca</li>
                  <li>Ele também evita recomendar exatamente a mesma peça novamente</li>
                </ul>
                <p class="mb-0">
                  O objetivo é aumentar o ticket médio e a conversão, mostrando opções que fazem sentido com o estilo e o histórico do cliente — como um vendedor experiente faria na loja física.
                </p>
              </div>
            </div>
          </div>

          <!-- ETAPA 1: Template WhatsApp -->
          <div v-show="etapaAtual === 1" class="etapa-content">
            <b-form-group label="Selecione o Template WhatsApp" label-class="font-weight-bold">
              <div class="template-selector">
                <div v-if="!templateSelecionado" class="template-empty" @click="abrirTemplate">
                  <feather-icon icon="FileTextIcon" size="32" class="text-muted mb-2" />
                  <p class="mb-1"><strong>Selecionar Template</strong></p>
                  <small class="text-muted">Clique para escolher um template</small>
                </div>
                <div v-else class="template-selected">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <feather-icon icon="CheckCircleIcon" size="20" class="text-success mr-2" />
                      <div>
                        <strong>{{ templateSelecionado.name }}</strong>
                        <br>
                        <small class="text-muted">Template selecionado</small>
                        <br v-if="quantidadeProdutos != null">
                        <small v-if="quantidadeProdutos != null" class="text-primary font-weight-bold">
                          {{ quantidadeProdutos }} produto(s) serão recomendados ({{ quantidadeProdutos }} card{{ quantidadeProdutos !== 1 ? 's' : '' }} no carrossel)
                        </small>
                      </div>
                    </div>
                    <div>
                      <b-button variant="outline-primary" size="sm" @click="abrirTemplate">
                        <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
                        Trocar
                      </b-button>
                      <b-button variant="outline-danger" size="sm" class="ml-1" @click="removerTemplate">
                        <feather-icon icon="TrashIcon" size="14" />
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </b-form-group>

            <div class="info-box mt-4">
              <feather-icon icon="InfoIcon" size="20" class="text-primary mr-2" />
              <div>
                <strong>Importante sobre Templates</strong>
                <p class="mb-0 mt-2">
                  Apenas <strong>templates de carrossel</strong> são exibidos. O template será usado como mensagem inicial antes do carrossel de produtos.
                  Certifique-se de que o template está aprovado pelo WhatsApp.
                </p>
              </div>
            </div>
          </div>

          <!-- ETAPA 2: Agendamento -->
          <div v-show="etapaAtual === 2" class="etapa-content">
            <!-- Dias da Semana -->
            <b-form-group label="Dias da Semana" label-class="font-weight-bold">
              <div class="dias-semana-container">
                <b-form-checkbox-group
                  v-model="form.diasSemana"
                  :options="diasSemanaOptions"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  class="dias-checkbox-group"
                />
              </div>
              <small class="text-muted">Selecione os dias em que a campanha deve ser executada</small>
            </b-form-group>

            <!-- Horário de Envio -->
            <b-form-group label="Horário de Envio" label-class="font-weight-bold" class="mt-4">
              <b-form-input
                id="send-time"
                v-model="form.sendTime"
                type="time"
                size="sm"
                placeholder="Ex: 10:00"
              />
              <small class="text-muted">Horário do dia para envio das mensagens</small>
            </b-form-group>

            <!-- Limite diário de envios -->
            <b-form-group label="Limite de envios por dia" label-class="font-weight-bold" class="mt-4">
              <b-form-input
                v-model.number="form.dailySendLimit"
                type="number"
                min="1"
                max="1000"
                placeholder="Ex: 100"
                size="sm"
              />
              <small class="text-muted">Número máximo de recomendações enviadas por dia</small>
            </b-form-group>

            <!-- Recomendar a cada (apenas meses) -->
            <b-form-group class="mt-4">
              <template #label>
                <span class="font-weight-bold">Frequência por cliente</span>
                <b-button
                  v-b-popover.hover.top="'Define com que frequência a lista de produtos recomendados para cada cliente será recalculada pela IA. Ex.: a cada 2 meses o sistema gera novas sugestões baseadas no histórico e no estilo do cliente.'"
                  variant="link"
                  class="p-0 ml-1 align-middle"
                  size="sm"
                >
                  <feather-icon icon="HelpCircleIcon" size="16" class="text-muted" />
                </b-button>
              </template>
              <b-row>
                <b-col md="4">
                  <b-form-input
                    v-model.number="form.regenerateEveryValue"
                    type="number"
                    min="1"
                    max="24"
                    placeholder="Ex: 2"
                    size="sm"
                  />
                </b-col>
                <b-col md="8">
                  <b-form-select
                    v-model="form.regenerateEveryUnit"
                    :options="regenerateEveryUnitOptions"
                    size="sm"
                  />
                </b-col>
              </b-row>
            </b-form-group>

            <div class="info-box mt-4">
              <feather-icon icon="InfoIcon" size="20" class="text-primary mr-2" />
              <div>
                <strong>Dica de Agendamento</strong>
                <p class="mb-0 mt-2">
                  Recomendamos enviar as mensagens durante o horário comercial (9h às 18h) e
                  limitar a frequência para evitar saturação dos clientes.
                </p>
              </div>
            </div>
          </div>

          <!-- ETAPA 3: Revisão e Ativação -->
          <div v-show="etapaAtual === 3" class="etapa-content">
            <div class="revisao-section">
              <h6 class="mb-3">
                <feather-icon icon="FileTextIcon" size="18" class="mr-2" />
                Resumo da Configuração
              </h6>

              <div class="revisao-item">
                <strong>Tipo de Mensagem:</strong>
                <span class="ml-2">{{ form.tipoMensagem }}</span>
              </div>

              <div class="revisao-item">
                <strong>Template:</strong>
                <span class="ml-2">{{ templateSelecionado ? templateSelecionado.name : 'Não selecionado' }}</span>
              </div>

              <div class="revisao-item">
                <strong>Dias da Semana:</strong>
                <span class="ml-2">{{ form.diasSemana.length > 0 ? form.diasSemana.join(', ') : 'Não selecionado' }}</span>
              </div>

              <div class="revisao-item">
                <strong>Horário de envio:</strong>
                <span class="ml-2">{{ form.sendTime || '--:--' }}</span>
              </div>

              <div class="revisao-item">
                <strong>Limite diário:</strong>
                <span class="ml-2">{{ form.dailySendLimit || '—' }} envios/dia</span>
              </div>

              <div class="revisao-item">
                <strong>Recomendar a cada:</strong>
                <span class="ml-2">{{ form.regenerateEveryValue || '—' }} {{ form.regenerateEveryUnit === 'months' ? 'meses' : form.regenerateEveryUnit }}</span>
              </div>

              <div class="revisao-item">
                <strong>Loja:</strong>
                <span class="ml-2">{{ nomeLojaSelecionada }}</span>
              </div>
            </div>

            <!-- Métricas da Campanha -->
            <div v-if="metricasCampanha" class="metricas-section mt-3">
              <b-card class="metricas-card">
                <h6 class="mb-3">
                  <feather-icon icon="BarChartIcon" size="18" class="mr-2" />
                  Métricas das Recomendações
                  <b-badge v-if="$route.query.metricas === 'true'" variant="success" class="ml-2">
                    Carregadas automaticamente
                  </b-badge>
                </h6>
                
                <b-row>
                  <b-col md="6" class="mb-3">
                    <div class="metrica-item">
                      <h4 class="text-primary mb-1">{{ metricasCampanha.summary.recomendacoes_criadas }}</h4>
                      <small class="text-muted">Recomendações Criadas</small>
                    </div>
                  </b-col>
                  <b-col md="6" class="mb-3">
                    <div class="metrica-item">
                      <h4 class="text-info mb-1">{{ metricasCampanha.summary.para_quantos_clientes }}</h4>
                      <small class="text-muted">Clientes Alcançados</small>
                    </div>
                  </b-col>
                </b-row>

                <hr>

                <b-row>
                  <b-col md="4" class="mb-2">
                    <div class="metrica-item">
                      <h5 class="text-success mb-1">{{ metricasCampanha.conversions.total_clicks }}</h5>
                      <small class="text-muted">Total de Cliques</small>
                    </div>
                  </b-col>
                  <b-col md="4" class="mb-2">
                    <div class="metrica-item">
                      <h5 class="text-warning mb-1">{{ metricasCampanha.conversions.direct_purchases }}</h5>
                      <small class="text-muted">Compras Diretas</small>
                    </div>
                  </b-col>
                  <b-col md="4" class="mb-2">
                    <div class="metrica-item">
                      <h5 class="text-info mb-1">{{ metricasCampanha.conversions.incentive_purchases }}</h5>
                      <small class="text-muted">Compras com Incentivo</small>
                    </div>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="6" class="mb-2">
                    <div class="metrica-item">
                      <h5 class="text-primary mb-1">{{ metricasCampanha.conversions.total_purchases }}</h5>
                      <small class="text-muted">Total de Compras</small>
                    </div>
                  </b-col>
                  <b-col md="6" class="mb-2">
                    <div class="metrica-item">
                      <h5 class="text-success mb-1">R$ {{ formatarMoeda(metricasCampanha.conversions.total_revenue) }}</h5>
                      <small class="text-muted">Receita Total</small>
                    </div>
                  </b-col>
                </b-row>
              </b-card>
            </div>
          </div>

          <!-- Actions -->
          <div class="actions-section mt-4 pt-3 border-top">
            <div class="d-flex justify-content-between align-items-center">
              <b-button
                v-if="etapaAtual > 0"
                variant="outline-secondary"
                @click="voltarEtapa"
              >
                <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
                Voltar
              </b-button>
              <b-button
                v-else
                variant="outline-secondary"
                @click="voltarParaLista"
              >
                <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
                Cancelar
              </b-button>
              
              <div class="d-flex gap-2">
                <b-button
                  v-if="etapaAtual < steps.length - 1"
                  variant="primary"
                  @click="proximaEtapa"
                  :disabled="!podeAvancar"
                >
                  Próximo
                  <feather-icon icon="ArrowRightIcon" size="16" class="ml-50" />
                </b-button>
                <b-button
                  v-else-if="!statusCampanha || !statusCampanha.ativo"
                  variant="success"
                  @click="ativarCampanha"
                  :disabled="!podeAtivar || carregando"
                >
                  <b-spinner v-if="carregando" small class="mr-50" />
                  <feather-icon v-else icon="PlayIcon" size="16" class="mr-50" />
                  {{ carregando ? 'Ativando campanha...' : 'Ativar Campanha' }}
                </b-button>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Coluna de Preview (oculta quando campanha está ativa / tela de desativar) -->
      <b-col v-if="!statusCampanha || !statusCampanha.ativo" lg="5" class="mb-3">
        <b-card class="preview-card sticky-preview shadow-sm">
          <h5 class="card-section-title mb-3">
            <feather-icon icon="EyeIcon" size="20" class="mr-2" />
            Preview WhatsApp
          </h5>

          <div class="whatsapp-preview">
            <!-- Header do WhatsApp -->
            <div class="whatsapp-header">
              <b-avatar
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS7ggnKgoIVGb-SIzY2kgRpF6mpRMANB7BHDA&s"
                size="40"
              />
              <div class="ml-2">
                <strong class="d-block">ManyConvert</strong>
                <small class="text-muted">online</small>
              </div>
            </div>

            <!-- Preview da Mensagem (se houver template) - ANTES do carrossel -->
            <div v-if="templateSelecionado" class="message-preview mb-3">
              <div class="message-bubble">
                <div class="message-text">
                  {{ previewMensagem }}
                </div>
                <div class="message-time">
                  {{ horaAtual }}
                </div>
              </div>
            </div>

            <!-- Carrossel de Produtos (preview com dados do template) -->
            <div v-if="form.tipoMensagem === 'Carrossel' && carouselCards.length" class="carousel-preview">
              <div class="carousel-scroll">
                <div
                  v-for="(card, idx) in carouselCards"
                  :key="idx"
                  class="carousel-item-preview"
                >
                  <div class="carousel-image">
                    <img
                      v-if="card.imageUrl"
                      :src="card.imageUrl"
                      alt="Preview produto"
                      class="carousel-image-img"
                    >
                    <feather-icon v-else icon="ImageIcon" size="48" class="text-muted" />
                  </div>
                  <div class="carousel-info">
                    <p class="product-name mb-1">Produto {{ idx + 1 }} (exemplo)</p>
                    <div class="carousel-buttons">
                      <a
                        v-for="(btn, bi) in card.buttons"
                        :key="bi"
                        :href="btn.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="carousel-btn"
                      >
                        {{ btn.text }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-center mt-2">
                <small class="text-muted">← Deslize para ver mais →</small>
              </div>
            </div>

            <!-- Aviso: preview é apenas exemplo -->
            <div v-if="templateSelecionado && carouselCards.length" class="preview-disclaimer mt-3">
              <b-alert variant="light" show class="mb-0 py-2">
                <small class="text-muted">
                  <feather-icon icon="InfoIcon" size="14" class="mr-1" />
                  Este preview é apenas um <strong>exemplo</strong> de como a mensagem será enviada. Os produtos exibidos na campanha serão definidos pela <strong>IA da ManyConvert</strong>, conforme o perfil de cada cliente.
                </small>
              </b-alert>
            </div>

            <!-- Empty State -->
            <div v-if="!templateSelecionado" class="empty-preview">
              <feather-icon icon="MessageSquareIcon" size="48" class="text-muted mb-2" />
              <p class="text-muted mb-0">Selecione um template para visualizar</p>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal de Templates -->
    <modal-selecao-templates
      v-model="modalTemplateVisivel"
      :templates="templatesDisponiveis"
      :carregando="false"
      @template-selecionado="onTemplateSelecionado"
    >
      <template #info-text>
        <strong>Apenas templates de carrossel</strong> aprovados pelo WhatsApp estão disponíveis para esta campanha de recomendação.
      </template>
      <template #empty-text>
        Crie templates e aguarde a aprovação do WhatsApp para utilizá-los em campanhas.
      </template>
    </modal-selecao-templates>
    </template>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormGroup, BFormRadioGroup, BFormCheckboxGroup,
  BFormInput, BFormSelect, BRow, BCol, BInputGroup, BInputGroupAppend, BInputGroupText,
  BBreadcrumb, BBreadcrumbItem, BAvatar, BAlert, BSpinner, BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { listarTemplates, buscaChatsTeste } from '@/functions/basics/chat'
import { verificarStatusRecomendacoes, ativarRecomendacoes, desativarRecomendacoes, buscarMetricasRecomendacoes } from '@/functions/basics/recommendations'
import ModalSelecaoTemplates from '@core/components/modal-selecao-templates/ModalSelecaoTemplates.vue'

export default {
  name: 'RecomendacaoProdutos',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormRadioGroup,
    BFormCheckboxGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupText,
    BBreadcrumb,
    BBreadcrumbItem,
    BAvatar,
    BAlert,
    BSpinner,
    BBadge,
    FeatherIcon,
    ModalSelecaoTemplates,
  },
  data() {
    return {
      etapaAtual: 0,
      steps: [
        {
          titulo: 'Informações Básicas',
          descricao: 'Tipo de mensagem',
        },
        {
          titulo: 'Template',
          descricao: 'Selecione o template',
        },
        {
          titulo: 'Agendamento',
          descricao: 'Configure dias e horários',
        },
        {
          titulo: 'Revisar e Ativar',
          descricao: 'Confirme e ative',
        },
      ],
      form: {
        tipoMensagem: 'Carrossel',
        diasSemana: [],
        sendTime: null,
        dailySendLimit: 100,
        regenerateEveryValue: 2,
        regenerateEveryUnit: 'months',
      },
      regenerateEveryUnitOptions: [
        { text: 'Meses', value: 'months' },
      ],
      tipoMensagemOptions: [
        { text: 'Carrossel de Produtos', value: 'Carrossel' },
      ],
      diasSemanaOptions: [
        { text: 'Seg', value: 'segunda' },
        { text: 'Ter', value: 'terca' },
        { text: 'Qua', value: 'quarta' },
        { text: 'Qui', value: 'quinta' },
        { text: 'Sex', value: 'sexta' },
        { text: 'Sáb', value: 'sabado' },
        { text: 'Dom', value: 'domingo' },
      ],
      // API espera send_days: "0,1,2,3,4,5,6" (0=Dom, 6=Sáb)
      diasSemanaParaNumero: {
        domingo: 0,
        segunda: 1,
        terca: 2,
        quarta: 3,
        quinta: 4,
        sexta: 5,
        sabado: 6,
      },
      modalTemplateVisivel: false,
      templatesDisponiveis: [],
      chatSelecionado: null,
      templateSelecionado: null,
      lojaSelecionada: null,
      nomeLoja: '',
      statusCampanha: null,
      metricasCampanha: null,
      carregando: false,
      statusCarregado: false,
    }
  },
  computed: {
    horaAtual() {
      const agora = new Date()
      return `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`
    },
    previewMensagem() {
      const body = this.templateBody
      if (body) return body
      if (!this.templateSelecionado) return ''
      return this.templateSelecionado.content || this.templateSelecionado.text || 'Mensagem do template'
    },
    templateBody() {
      const t = this.templateSelecionado?.template || this.templateSelecionado
      if (!t?.components) return null
      const body = t.components.find(c => c.type === 'BODY')
      return body?.text || null
    },
    carouselComponent() {
      const t = this.templateSelecionado?.template || this.templateSelecionado
      if (!t?.components) return null
      return t.components.find(c => c.type === 'CAROUSEL') || null
    },
    carouselCards() {
      const carousel = this.carouselComponent
      if (!carousel?.cards || !carousel.cards.length) return []
      return carousel.cards.map(card => {
        const header = card.components?.find(c => c.type === 'HEADER' && c.format === 'IMAGE')
        const buttonsComp = card.components?.find(c => c.type === 'BUTTONS')
        const imageUrl = header?.example?.header_handle?.[0] || null
        const buttons = (buttonsComp?.buttons || []).map(btn => {
          const text = btn.text || 'Link'
          let href = btn.url || '#'
          if (btn.example && btn.example[0] && typeof href === 'string') {
            href = href.replace(/\{\{1\}\}/g, btn.example[0])
          }
          return { text, href }
        })
        return { imageUrl, buttons }
      })
    },
    quantidadeProdutos() {
      const cards = this.carouselCards
      return cards.length > 0 ? cards.length : null
    },
    nomeLojaSelecionada() {
      return this.nomeLoja || this.lojaSelecionada || 'Carregando...'
    },
    podeAvancar() {
      if (this.etapaAtual === 0) {
        return this.form.tipoMensagem && this.lojaSelecionada
      }
      if (this.etapaAtual === 1) {
        return this.templateSelecionado !== null
      }
      if (this.etapaAtual === 2) {
        return (
          this.form.diasSemana.length > 0 &&
          this.form.sendTime &&
          this.form.dailySendLimit > 0 &&
          this.form.regenerateEveryValue > 0 &&
          this.form.regenerateEveryUnit
        )
      }
      return true
    },
    podeAtivar() {
      return (
        this.lojaSelecionada &&
        this.templateSelecionado &&
        this.form.diasSemana.length > 0 &&
        this.form.sendTime &&
        this.form.dailySendLimit > 0 &&
        this.form.regenerateEveryValue > 0 &&
        this.form.regenerateEveryUnit &&
        (!this.statusCampanha || !this.statusCampanha.ativo)
      )
    },
    campanhaAtiva() {
      // Retorna a primeira campanha ativa da lista
      if (this.statusCampanha && this.statusCampanha.campanhas && this.statusCampanha.campanhas.length > 0) {
        const campanha = this.statusCampanha.campanhas.find(c => c.is_active) || this.statusCampanha.campanhas[0]
        return campanha
      }
      return null
    },
  },
  async created() {
    // Verificar se há loja selecionada na query
    if (this.$route.query.loja) {
      this.lojaSelecionada = this.$route.query.loja
      // Se houver nome da loja na query, usar ele
      if (this.$route.query.nomeLoja) {
        this.nomeLoja = this.$route.query.nomeLoja
      }
    } else {
      // Tentar carregar do localStorage
      const lojaSalva = localStorage.getItem('lojaSelecionada')
      if (lojaSalva) {
        this.lojaSelecionada = lojaSalva
      }
      const nomeLojaSalva = localStorage.getItem('nomeLojaSelecionada')
      if (nomeLojaSalva) {
        this.nomeLoja = nomeLojaSalva
      }
    }

    await this.carregarDadosIniciais()
    
    // Se veio do botão "Ver Métricas", carregar métricas automaticamente
    if (this.$route.query.metricas === 'true') {
      await this.carregarMetricas()
    }
  },
  methods: {
    navegarParaEtapa(index) {
      // Só permite navegar para etapas anteriores ou para a próxima se estiver válido
      if (index < this.etapaAtual) {
        this.etapaAtual = index
      } else if (index === this.etapaAtual + 1 && this.podeAvancar) {
        this.etapaAtual = index
      }
    },

    proximaEtapa() {
      if (this.podeAvancar && this.etapaAtual < this.steps.length - 1) {
        this.etapaAtual++
        // Scroll suave para o topo do card
        this.$nextTick(() => {
          const configCard = document.querySelector('.config-card')
          if (configCard) {
            configCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },

    voltarEtapa() {
      if (this.etapaAtual > 0) {
        this.etapaAtual--
      }
    },

    async carregarDadosIniciais() {
      this.carregando = true
      try {
        // Verificar se há loja selecionada
        if (!this.lojaSelecionada) {
          this.statusCarregado = true
          this.$bvToast.toast('Nenhuma loja selecionada. Retorne à página anterior e selecione uma loja.', {
            title: 'Loja Obrigatória',
            variant: 'warning',
            solid: true,
          })
          setTimeout(() => {
            this.$router.push({ name: 'campaign-ia' })
          }, 2000)
          return
        }
        
        // Carregar chats
        await this.buscarChatDisponivel()
        
        // Carregar status da campanha (obrigatório antes de exibir a página)
        await this.carregarStatusCampanha()
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error)
        this.$bvToast.toast('Erro ao carregar dados iniciais', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
        this.statusCarregado = true
      }
    },

    irParaMetricas() {
      const query = { loja: this.lojaSelecionada }
      if (this.nomeLoja) query.nomeLoja = this.nomeLoja
      this.$router.push({ name: 'campaign-ia-metricas', query })
    },


    async buscarChatDisponivel() {
      try {
        const contas = await buscaChatsTeste()
        if (contas.chats && contas.chats.length > 0) {
          this.chatSelecionado = contas.chats[0].chat_token
        }
      } catch (error) {
        console.error('Erro ao buscar chats:', error)
      }
    },


    async carregarStatusCampanha() {
      if (!this.lojaSelecionada) return
      
      try {
        const response = await verificarStatusRecomendacoes(this.lojaSelecionada)
        console.log('Status da campanha (response completa):', response)
        
        // A API retorna { success: true, data: {...} }
        const status = response.data || response
        console.log('Status da campanha (data):', status)
        
        // Verificar se recommendations_active é true (boolean)
        const isAtivo = status.recommendations_active === true
        
        // A API retorna campaign (singular), não campaigns (plural)
        const campanhas = status.campaign ? [status.campaign] : []
        
        this.statusCampanha = {
          ativo: isAtivo,
          ultimaAtualizacao: status.updated_at || status.ultima_atualizacao || null,
          configuracao: status.campaign || status.configuracao || null,
          campanhas: campanhas,
          activeCampaigns: status.campaign && status.campaign.is_active ? 1 : 0,
        }
        
        console.log('Status processado:', this.statusCampanha)
        console.log('📋 Campanhas array:', this.statusCampanha.campanhas)
        if (this.statusCampanha.campanhas.length > 0) {
          console.log('🎯 Primeira campanha:', this.statusCampanha.campanhas[0])
        }
      } catch (error) {
        console.error('Erro ao carregar status da campanha:', error)
        // Se não conseguir carregar, assume que está inativa
        this.statusCampanha = {
          ativo: false,
          ultimaAtualizacao: null,
          configuracao: null,
          campanhas: [],
          activeCampaigns: 0,
        }
      }
    },

    async desativarCampanha() {
      if (!this.lojaSelecionada) {
        this.$bvToast.toast('Selecione uma loja primeiro', {
          title: 'Loja Obrigatória',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const confirmarDesativacao = await this.$bvModal.msgBoxConfirm(
        'Deseja realmente desativar as recomendações? A campanha deixará de enviar novas mensagens.',
        {
          title: 'Confirmar desativação',
          okTitle: 'Sim, desativar',
          cancelTitle: 'Cancelar',
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          centered: true,
          size: 'md',
        }
      )
      if (!confirmarDesativacao) return

      this.carregando = true
      try {
        await desativarRecomendacoes(this.lojaSelecionada)
        
        this.$bvToast.toast('Campanha desativada com sucesso!', {
          title: 'Campanha Desativada',
          variant: 'success',
          solid: true,
        })
        
        // Recarregar status
        await this.carregarStatusCampanha()
      } catch (error) {
        console.error('Erro ao desativar campanha:', error)
        this.$bvToast.toast('Erro ao desativar a campanha', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },

    formatarData(data) {
      if (!data) return ''
      return new Date(data).toLocaleString('pt-BR')
    },

    formatarHorario(horario) {
      if (!horario) return '—'
      // Se for um timestamp, extrair apenas a hora
      if (typeof horario === 'string' && horario.includes('T')) {
        try {
          const date = new Date(horario)
          return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        } catch {
          return '—'
        }
      }
      // Se já estiver no formato HH:MM, retornar direto
      if (typeof horario === 'string' && /^\d{2}:\d{2}/.test(horario)) {
        return horario.substring(0, 5)
      }
      return horario
    },

    formatarHorarioCompleto(horario) {
      if (!horario) return '—'
      try {
        const date = new Date(horario)
        const hoje = new Date()
        
        // Se for hoje, mostrar apenas a hora
        if (date.toDateString() === hoje.toDateString()) {
          return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        }
        
        // Se for outro dia, mostrar dia da semana e hora
        const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
        const diaSemana = diasSemana[date.getDay()]
        const hora = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        
        return `${diaSemana} às ${hora}`
      } catch {
        return '—'
      }
    },

    formatarDiasSemana(sendDays) {
      if (!sendDays) return 'Todos os dias'
      
      const diasSemana = {
        0: 'Dom',
        1: 'Seg',
        2: 'Ter',
        3: 'Qua',
        4: 'Qui',
        5: 'Sex',
        6: 'Sáb'
      }
      
      try {
        // sendDays vem como string "1,3,5"
        const dias = sendDays.split(',').map(d => d.trim())
        const diasFormatados = dias.map(d => diasSemana[d] || d)
        
        // Se for todos os dias
        if (dias.length === 7) {
          return 'Todos os dias'
        }
        
        // Se for apenas dias úteis (1-5)
        if (dias.length === 5 && dias.every(d => ['1', '2', '3', '4', '5'].includes(d))) {
          return 'Dias úteis'
        }
        
        return diasFormatados.join(', ')
      } catch {
        return sendDays
      }
    },

    traduzirAlgoritmo(algoritmo) {
      if (!algoritmo) return 'Híbrido'
      const traducoes = {
        'hybrid': 'Híbrido',
        'user-based': 'Baseado em Usuário',
        'item-based': 'Baseado em Item'
      }
      return traducoes[algoritmo] || algoritmo
    },

    formatarMoeda(valor) {
      if (!valor) return '0,00'
      return parseFloat(valor).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    async carregarMetricas() {
      if (!this.lojaSelecionada) return

      this.carregando = true
      try {
        const response = await buscarMetricasRecomendacoes(this.lojaSelecionada)
        this.metricasCampanha = response.data
        this.$bvToast.toast('Métricas carregadas com sucesso!', {
          title: 'Métricas',
          variant: 'success',
          solid: true,
        })
        
        // Se veio do botão "Ver Métricas", fazer scroll para a seção de métricas
        if (this.$route.query.metricas === 'true') {
          this.$nextTick(() => {
            const metricasSection = document.querySelector('.metricas-section')
            if (metricasSection) {
              metricasSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              })
            }
          })
        }
      } catch (error) {
        console.error('Erro ao carregar métricas:', error)
        this.$bvToast.toast('Erro ao carregar métricas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },
    async abrirTemplate() {
      try {
        if (!this.chatSelecionado) {
          this.$bvToast.toast('Nenhuma conta do WhatsApp encontrada.', {
            title: 'Erro',
            variant: 'warning',
            solid: true,
          })
          return
        }

        const resp = await listarTemplates(this.chatSelecionado, true) // Apenas aprovados
        const todos = resp.templates || []
        // Apenas templates de carrossel (type CAROUSEL) para recomendação de produtos
        this.templatesDisponiveis = todos.filter(t =>
          t.components && t.components.some(c => c.type === 'CAROUSEL')
        )
        if (this.templatesDisponiveis.length === 0) {
          this.$bvToast.toast('Nenhum template de carrossel aprovado encontrado. Crie e aprova um template do tipo carrossel no WhatsApp.', {
            title: 'Apenas templates de carrossel',
            variant: 'warning',
            solid: true,
          })
          return
        }
        this.modalTemplateVisivel = true
      } catch (error) {
        this.$bvToast.toast('Erro ao carregar templates.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    onTemplateSelecionado(templateData) {
      this.templateSelecionado = templateData
      const qtd = this.quantidadeProdutos
      const msg = qtd != null
        ? `Template "${templateData.name}" selecionado! ${qtd} produto(s) serão recomendados no carrossel.`
        : `Template "${templateData.name}" selecionado!`
      this.$bvToast.toast(msg, {
        title: 'Template Selecionado',
        variant: 'success',
        solid: true,
      })
    },
    removerTemplate() {
      this.templateSelecionado = null
      this.$bvToast.toast('Template removido!', {
        title: 'Removido',
        variant: 'info',
        solid: true,
      })
    },
    voltarParaLista() {
      this.$router.push({ name: 'campaign-ia' })
    },
    async ativarCampanha() {
      // Validações
      if (!this.lojaSelecionada) {
        this.$bvToast.toast('Selecione uma loja para continuar', {
          title: 'Loja Obrigatória',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!this.templateSelecionado) {
        this.$bvToast.toast('Selecione um template para continuar', {
          title: 'Template Obrigatório',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!this.form.diasSemana || this.form.diasSemana.length === 0) {
        this.$bvToast.toast('Selecione pelo menos um dia da semana', {
          title: 'Dias da Semana Obrigatórios',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!this.form.sendTime) {
        this.$bvToast.toast('Defina o horário de envio', {
          title: 'Horário Obrigatório',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!this.form.dailySendLimit || this.form.dailySendLimit < 1) {
        this.$bvToast.toast('Defina o limite de envios por dia', {
          title: 'Limite Diário Obrigatório',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!this.form.regenerateEveryValue || this.form.regenerateEveryValue < 1) {
        this.$bvToast.toast('Defina o intervalo de regeneração', {
          title: 'Regeneração Obrigatória',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const confirmarAtivacao = await this.$bvModal.msgBoxConfirm(
        'Deseja realmente ativar a campanha de recomendação?\n\n' +
        'Atenção: a campanha entrará em treinamento e o primeiro disparo só ocorrerá no próximo dia programado para envio, no horário que você configurou.',
        {
          title: 'Confirmar ativação',
          okTitle: 'Sim, ativar',
          cancelTitle: 'Cancelar',
          okVariant: 'success',
          cancelVariant: 'outline-secondary',
          centered: true,
          size: 'md',
        }
      )
      if (!confirmarAtivacao) return

      this.carregando = true
      try {
        // Preparar configuração conforme API
        const configuracao = {
          template_name: this.templateSelecionado.name,
          cards_count: this.quantidadeProdutos ?? 0,
          send_days: this.form.diasSemana
            .map(d => this.diasSemanaParaNumero[d])
            .filter(n => n !== undefined)
            .sort((a, b) => a - b)
            .join(','),
          send_time: this.form.sendTime,
          max_recommendations_per_week: 300,
          daily_send_limit: parseInt(this.form.dailySendLimit, 10),
          regenerate_every_value: parseInt(this.form.regenerateEveryValue, 10),
          regenerate_every_unit: this.form.regenerateEveryUnit,
        }

        // Ativar recomendações
        await ativarRecomendacoes(this.lojaSelecionada, configuracao)
        
        this.$bvToast.toast('Campanha ativada com sucesso!', {
          title: 'Campanha Ativa',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        // Recarregar status
        await this.carregarStatusCampanha()

        // Redirecionar após 2 segundos
        setTimeout(() => {
          this.$router.push({ name: 'campaign-ia' })
        }, 2000)
      } catch (error) {
        console.error('Erro ao ativar campanha:', error)
        this.$bvToast.toast('Erro ao ativar campanha', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },
  },
}
</script>

<style scoped>
.recomendacao-produtos-container {
  padding: 2rem;
  min-height: 100vh;
  background: #f8f9fa;
}

.status-loading {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-top: 1rem;
}

/* Header */
.page-header {
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.breadcrumb-custom {
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

/* Steps Card - Estilo Simples */
.steps-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.steps-header h6 {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.steps-navigation {
  display: flex;
  gap: 0.5rem;
}

.step-indicator {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e8e8e8;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.step-indicator.step-completed {
  background: #28c76f;
}

.step-indicator.step-active {
  background: #9A3092;
  animation: progressPulse 1.5s ease-in-out infinite;
}

@keyframes progressPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.step-indicator.step-pending {
  background: #e8e8e8;
}

/* Step Header dentro do Card */
.step-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
}

.step-number-badge {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #9A3092 0%, #61185b 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  flex-shrink: 0;
}

.step-header h5 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #5e5873;
}

.step-header small {
  font-size: 0.875rem;
  display: block;
  margin-top: 0.25rem;
}

/* Etapas Content */
.etapa-content {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-box {
  background: #f8f7ff;
  border: 1px solid #e0dbff;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: flex-start;
}

.info-box p {
  font-size: 0.9rem;
  color: #5e5873;
  line-height: 1.6;
}

/* Revisão Section */
.revisao-section {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
}

.revisao-item {
  padding: 0.75rem;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.95rem;
}

.revisao-item:last-child {
  border-bottom: none;
}

.revisao-item strong {
  color: #5e5873;
  display: inline-block;
  min-width: 140px;
}

.revisao-item span {
  color: #6e6b7b;
}

/* Cards */
.config-card,
.preview-card {
  border-radius: 12px;
  border: none;
  position: relative;
}

/* Campaign Lock Overlay */
.card-locked {
  overflow: hidden;
}

.campaign-lock-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.lock-content {
  text-align: center;
  max-width: 500px;
}

.lock-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(255, 193, 7, 0.3);
  animation: pulse-lock 2s infinite;
}

@keyframes pulse-lock {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(255, 193, 7, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(255, 193, 7, 0.4);
  }
}

.lock-content h4 {
  color: #5e5873;
  font-weight: 600;
}

.lock-content p {
  font-size: 1rem;
  line-height: 1.6;
}

.lock-content .gap-3 {
  gap: 1rem;
}

.card-section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f0f0f0;
}

/* Template Selector */
.template-selector {
  margin-top: 0.5rem;
}

.template-empty {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.template-empty:hover {
  border-color: #9A3092;
  background: #f8f7ff;
}

.template-selected {
  border: 2px solid #28c76f;
  border-radius: 8px;
  padding: 1rem;
  background: #f0fff4;
}

.status-section {
  padding: 1rem;
}

/* Campaign Active Alert */
.campaign-active-alert {
  border-radius: 12px;
  border: 2px solid #28c76f;
  background: linear-gradient(135deg, #f0fff4 0%, #e8f5e9 100%);
  padding: 1.5rem;
}

.campaign-active-alert .status-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(40, 199, 111, 0.2);
}

.campaign-active-alert h5 {
  color: #28c76f;
  margin: 0;
}

/* Actions Section */
.actions-section {
  margin-top: 2rem;
}

.gap-2 {
  gap: 0.5rem;
}

/* Preview WhatsApp */
.sticky-preview {
  position: sticky;
  top: 1rem;
}

.whatsapp-preview {
  background: #ece5dd;
  border-radius: 12px;
  padding: 1rem;
  min-height: 500px;
}

.whatsapp-header {
  display: flex;
  align-items: center;
  background: white;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Carousel Preview */
.carousel-preview {
  margin-top: 1rem;
}

.carousel-scroll {
  display: flex;
  overflow-x: auto;
  gap: 0.75rem;
  padding: 0.5rem 0;
  scroll-snap-type: x mandatory;
}

.carousel-scroll::-webkit-scrollbar {
  height: 6px;
}

.carousel-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.carousel-scroll::-webkit-scrollbar-thumb {
  background: #9A3092 ;
  border-radius: 4px;
}

.carousel-item-preview {
  flex: 0 0 220px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  scroll-snap-align: start;
}

.carousel-image {
  width: 100%;
  height: 160px;
  background: linear-gradient(135deg, #9A3092  0%, #61185b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
}

.carousel-image-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-info {
  padding: 1rem;
}

.product-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.product-price {
  font-size: 1.1rem;
  font-weight: 700;
  color: #28c76f;
}

.carousel-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.carousel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: 1px solid #4fc3f7;
  border-radius: 6px;
  color: #4fc3f7;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
}

.carousel-btn:hover {
  background: rgba(79, 195, 247, 0.1);
}

/* Message Preview */
.message-preview {
  margin-top: 0;
  margin-bottom: 1rem;
}

.message-bubble {
  background: white;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  max-width: 85%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-text {
  font-size: 0.9rem;
  color: #333;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-time {
  font-size: 0.7rem;
  color: #999;
  text-align: right;
  margin-top: 0.5rem;
}

/* Empty State */
.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

/* Dias da Semana */
.dias-semana-container {
  margin-top: 0.5rem;
}

.dias-checkbox-group {
  display: flex;
  flex-wrap: wrap;
}

.dias-checkbox-group .btn {
  flex: 1;
  min-width: 60px;
  font-size: 0.85rem;
  padding: 0.5rem 0.25rem;
}

/* Loja Info */
.loja-info {
  border-radius: 8px;
  border: none;
  padding: 1rem;
  background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
}

/* Métricas */
.metricas-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.metrica-item {
  text-align: center;
  padding: 0.5rem;
  border-radius: 6px;
  background: white;
  border: 1px solid #f0f0f0;
}

.metrica-item h4,
.metrica-item h5 {
  font-weight: 700;
  margin: 0;
}

.metrica-item small {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Responsividade */
@media (max-width: 992px) {
  .sticky-preview {
    position: relative;
    top: 0;
  }
}

@media (max-width: 768px) {
  .recomendacao-produtos-container {
    padding: 1rem;
  }

  .page-header {
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .step-number-badge {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .step-header h5 {
    font-size: 1.1rem;
  }

  .actions-section .d-flex {
    flex-direction: column;
  }

  .actions-section .gap-2 {
    width: 100%;
  }

  .actions-section button {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

/* Estilos do Card de Campanha Ativa (estilo igual ao de Aniversário) */
.campanha-ativa-section {
  margin-bottom: 2rem;
}

.campanha-card {
  border-radius: 12px;
  border: none;
}

.status-badge-active {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28c76f 0%, #1f9d57 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.config-info {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.config-info .info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.config-info .info-item svg {
  flex-shrink: 0;
  margin-top: 0.25rem;
}
</style>

